import router from '@/router';
import { ErrorCodes } from './ErrorCodes';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

class Application {
    private readonly maxAuthFailedCount = 1;
    private _userManager: UserManager;
    private _user: User | null = null;

    public constructor(userManager: UserManager) {
        this._userManager = userManager;
    }

    private get authFailedCount(): number {
        const authFailedCountString = window.localStorage.getItem('auth-failed-count');

        if (!authFailedCountString) {
            return 0;
        }

        return parseInt(authFailedCountString) || 0;
    }

    private set authFailedCount(value: number) {
        window.localStorage.setItem('auth-failed-count', value.toString());
    }

    public signOutAsync() {
        return this._userManager.signoutRedirect();
    }

    public async startupAsync() {
        if (this.matchesPath(userManager.settings.silent_redirect_uri)) {
            await this._userManager.signinSilentCallback();
            return false;
        }

        if (this.matchesPath(this._userManager.settings.redirect_uri)) {
            router.replace(this._user?.url_state ?? '/');
        }

        this.handleManagerEvents();

        return await this.authAsync();
    }

    public async getAccessTokenAsync(): Promise<string> {
        if (!this._user?.access_token || this._user.expired) {
            await this.authAsync();
        }

        return this._user?.access_token as string;
    }

    public async authAsync() {
        const authFailedCount = this.authFailedCount;

        try {
            this._user = await this._userManager.getUser();

            if (!this._user || this._user.expired) {
                this._user = await this._userManager.signinSilent();
            }

            this.authFailedCount = 0;

            return true;
        } catch (error) {
            this.authFailedCount = authFailedCount + 1;

            if (authFailedCount >= this.maxAuthFailedCount) {
                this.authFailedCount = 0;
                throw new Error(ErrorCodes.TooManyFailedAuthorizationAttempts);
            }

            await this._userManager.signinRedirect({
                url_state: window.location.pathname + window.location.search,
            });

            return false;
        }
    }

    private handleManagerEvents() {
        this._userManager.events.addUserLoaded((newUser) => {
            this._user = newUser;
        });

        this._userManager.events.addUserUnloaded(() => {
            this._user = null;
        });

        this._userManager.events.addAccessTokenExpired(() => {
            this._user = null;
        });

        this._userManager.events.addSilentRenewError(() => {
            this.authAsync();
        });

        this._userManager.events.addUserSignedOut(() => {
            this._userManager.signinRedirect({
                url_state: window.location.pathname + window.location.search,
            });
        });
    }

    /**
     * Gets the path portion of a url.
     * @param url - full url
     * @returns
     */
    private getUrlPath(url: string) {
        const a = document.createElement('a');
        a.href = url;
        let p = a.pathname;
        if (p[0] !== '/') p = '/' + p;
        return p;
    }

    /**
     * Checks if current url's path matches given url's path.
     * @param {String} testUrl - url to test against.
     */
    private matchesPath(testUrl: string) {
        return window.location.pathname.toLocaleLowerCase() === this.getUrlPath(testUrl).toLocaleLowerCase();
    }
}

const appUrl = process.env.VUE_APP_URL;

const authName = process.env.VUE_APP_OIDC_AUTH_NAME;
const authority = process.env.VUE_APP_OIDC_AUTHORITY;
const clientId = process.env.VUE_APP_OIDC_CLIENT_ID;
const responseType = process.env.VUE_APP_OIDC_RESPONSE_TYPE;
const scope = process.env.VUE_APP_OIDC_SCOPE;

const config: UserManagerSettings = {
    monitorSession: true,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({
        store: sessionStorage,
    }),
    post_logout_redirect_uri: appUrl,
    redirect_uri: `${appUrl}auth/signinwin/${authName}`,
    silent_redirect_uri: `${appUrl}auth/signinsilent/${authName}`,
    authority,
    client_id: clientId,
    response_type: responseType,
    scope,
};

const userManager = new UserManager(config);

export default new Application(userManager);
